import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import RecurringScheduleModal from '../../../components/ui/RecurringScheduleModal';
import StarRating from '../../../components/ui/StarRating';
import RewardBadge from '../../../components/ui/RewardBadge';
import { getRecurringDescription } from '../../../utils/recurringUtils';

const AddTaskModal = ({ isOpen, onClose, onSave, editTask, familyMembers }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assignedTo: '',
    assignedColor: '',
    dueDate: '',
    priority: 'medium',
    status: 'not-started',
    category: '',
    progress: 0,
    stars: 0,
    reward: null,
    milestones: [],
    recurringConfig: null
  });

  const [milestoneInput, setMilestoneInput] = useState('');
  const [errors, setErrors] = useState({});
  const [isRecurringModalOpen, setIsRecurringModalOpen] = useState(false);

  useEffect(() => {
    if (editTask) {
      setFormData(editTask);
    } else {
      setFormData({
        title: '',
        description: '',
        assignedTo: '',
        assignedColor: '',
        dueDate: '',
        priority: 'medium',
        status: 'not-started',
        category: '',
        progress: 0,
        stars: 0,
        reward: null,
        milestones: [],
        recurringConfig: null
      });
    }
    setErrors({});
  }, [editTask, isOpen]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handlePersonChange = (personId) => {
    const person = familyMembers?.find(m => m?.id?.toString() === personId);
    if (person) {
      setFormData(prev => ({
        ...prev,
        assignedTo: person?.name,
        assignedColor: person?.color
      }));
    }
  };

  const handleAddMilestone = () => {
    if (milestoneInput?.trim()) {
      setFormData(prev => ({
        ...prev,
        milestones: [...prev?.milestones, { title: milestoneInput?.trim(), completed: false }]
      }));
      setMilestoneInput('');
    }
  };

  const handleRemoveMilestone = (index) => {
    setFormData(prev => ({
      ...prev,
      milestones: prev?.milestones?.filter((_, i) => i !== index)
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData?.title?.trim()) newErrors.title = 'Title is required';
    if (!formData?.assignedTo) newErrors.assignedTo = 'Please assign to a family member';
    if (!formData?.dueDate) newErrors.dueDate = 'Due date is required';
    if (!formData?.category?.trim()) newErrors.category = 'Category is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validate()) {
      onSave(formData);
      onClose();
    }
  };

  const handleRecurringSave = (recurringData) => {
    setFormData(prev => ({
      ...prev,
      recurringConfig: recurringData
    }));
  };

  if (!isOpen) return null;

  const personOptions = familyMembers?.map(member => ({
    value: member?.id?.toString(),
    label: member?.name
  }));

  const priorityOptions = [
    { value: 'high', label: 'High Priority' },
    { value: 'medium', label: 'Medium Priority' },
    { value: 'low', label: 'Low Priority' }
  ];

  const statusOptions = [
    { value: 'not-started', label: 'Not Started' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'on-hold', label: 'On Hold' },
    { value: 'completed', label: 'Completed' }
  ];

  return (
    <>
      <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
        <div
          className="bg-card rounded-xl border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          onClick={(e) => e?.stopPropagation()}
        >
          <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon name="target" size={20} className="text-primary" />
              </div>
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                {editTask ? 'Edit Task' : 'Add Long Term Task'}
              </h2>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <Icon name="x" size={20} />
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-4 md:space-y-6">
            <Input
              label="Task Title"
              type="text"
              placeholder="Enter task title"
              value={formData?.title}
              onChange={(e) => handleChange('title', e?.target?.value)}
              error={errors?.title}
              required
            />

            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Description</label>
              <textarea
                className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                rows="4"
                placeholder="Enter task description"
                value={formData?.description}
                onChange={(e) => handleChange('description', e?.target?.value)}
              />
            </div>

            <Select
              label="Assign To"
              options={personOptions}
              value={familyMembers?.find(m => m?.name === formData?.assignedTo)?.id?.toString() || ''}
              onChange={handlePersonChange}
              error={errors?.assignedTo}
              required
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Due Date"
                type="date"
                value={formData?.dueDate}
                onChange={(e) => handleChange('dueDate', e?.target?.value)}
                error={errors?.dueDate}
                required
              />

              <Input
                label="Category"
                type="text"
                placeholder="e.g., Home, Work, Personal"
                value={formData?.category}
                onChange={(e) => handleChange('category', e?.target?.value)}
                error={errors?.category}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Priority"
                options={priorityOptions}
                value={formData?.priority}
                onChange={(value) => handleChange('priority', value)}
              />

              <Select
                label="Status"
                options={statusOptions}
                value={formData?.status}
                onChange={(value) => handleChange('status', value)}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Progress: {formData?.progress}%</label>
              <input
                type="range"
                min="0"
                max="100"
                value={formData?.progress}
                onChange={(e) => handleChange('progress', parseInt(e?.target?.value))}
                className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, ${formData?.assignedColor || '#2563EB'} 0%, ${formData?.assignedColor || '#2563EB'} ${formData?.progress}%, #F1F5F9 ${formData?.progress}%, #F1F5F9 100%)`
                }}
              />
            </div>

            <div className="space-y-3">
              <label className="text-sm font-medium text-foreground">Milestones</label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  placeholder="Add milestone"
                  value={milestoneInput}
                  onChange={(e) => setMilestoneInput(e?.target?.value)}
                  onKeyPress={(e) => e?.key === 'Enter' && (e?.preventDefault(), handleAddMilestone())}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleAddMilestone}
                  iconName="plus"
                >
                  Add
                </Button>
              </div>
              {formData?.milestones?.length > 0 && (
                <div className="space-y-2">
                  {formData?.milestones?.map((milestone, index) => (
                    <div key={index} className="flex items-center justify-between bg-muted/50 px-3 py-2 rounded-lg">
                      <span className="text-sm text-foreground">{milestone?.title}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveMilestone(index)}
                        className="text-destructive hover:bg-destructive/10 p-1 rounded transition-smooth"
                      >
                        <Icon name="x" size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-3">
              <label className="block text-sm font-medium text-foreground">
                Fun Rewards (Optional)
              </label>
              
              <div className="space-y-2">
                <label className="text-xs text-muted-foreground">Star Rating</label>
                <StarRating 
                  value={formData?.stars} 
                  onChange={(value) => handleChange('stars', value)}
                  size={24}
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs text-muted-foreground">Reward Badge</label>
                <div className="flex flex-wrap gap-2">
                  {['trophy', 'medal', 'badge', 'star', 'crown', 'gem']?.map((rewardType) => (
                    <button
                      key={rewardType}
                      type="button"
                      onClick={() => handleChange('reward', formData?.reward === rewardType ? null : rewardType)}
                      className={`transition-smooth hover:scale-105 ${
                        formData?.reward === rewardType ? 'ring-2 ring-primary rounded-full' : ''
                      }`}
                    >
                      <RewardBadge reward={rewardType} size="default" />
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-foreground">
                Recurring Schedule
              </label>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsRecurringModalOpen(true)}
                className="w-full justify-between"
              >
                <div className="flex items-center gap-2">
                  <Icon name="repeat" size={16} />
                  <span className="text-sm">
                    {formData?.recurringConfig ? getRecurringDescription(formData?.recurringConfig) : 'Does not repeat'}
                  </span>
                </div>
                <Icon name="chevron-right" size={16} />
              </Button>
            </div>

            <div className="flex flex-col-reverse md:flex-row gap-3 pt-4 border-t border-border">
              <Button type="button" variant="outline" onClick={onClose} fullWidth>
                Cancel
              </Button>
              <Button type="submit" variant="default" fullWidth>
                {editTask ? 'Update Task' : 'Create Task'}
              </Button>
            </div>
          </form>
        </div>
      </div>
      <RecurringScheduleModal
        isOpen={isRecurringModalOpen}
        onClose={() => setIsRecurringModalOpen(false)}
        onSave={handleRecurringSave}
        initialData={formData?.recurringConfig}
      />
    </>
  );
};

export default AddTaskModal;